import { PayloadAction } from "@reduxjs/toolkit";
import { QueryParams } from "@router/route-params";
import { AppState, setIsAlphaTestingEnabled, setIsBetaTestingEnabled, setIsDevModeEnabled } from "@store/app/app-slice";
import { DevFeaturesState } from "@store/dev-features/dev-features-slice";

/** Allowed dev tool keys */
export type DevToolKey =
  | "openAdminPanel"
  | "clearCookies"
  | "resetFueFlags"
  | "sendTestSentryError"
  | "allowLocalizeTranslations"
  | "toggleDarkMode";

type DevFeatureBase = {
  /** Display name to show in the table */
  label: string;

  /** A description of what this feature or tool does */
  description: string;
};

export type SetEnabledAction = (isEnabled: boolean) => PayloadAction<boolean>;

type Feature = DevFeatureBase & {
  /** query param to enable/disable this feature */
  queryParam: QueryParams;

  /** Key name of the variable for this feature in the app state */
  appStateKey: keyof AppState;

  /** Redux action to update the feature's state */
  setEnabledAction: SetEnabledAction;
};

/** Control types for dev tools */
export type DevToolControlType = "button" | "toggle";

/** Type definition for the callback of toggle features or toggle tools */
export type TogglesCallback = (isEnabled: boolean) => void;

/** Type definition for the callback of button dev tools */
export type ButtonsCallback = () => void;

type DevTool = DevFeatureBase & {
  /** The action this tool performs (maps to a hook function) */
  action: DevToolKey;

  /** Button text */
  buttonText?: string;

  /** Control type for the dev tool, defaults to button */
  controlType: DevToolControlType;

  /**
   * Key name of the variable for this feature
   * in the dev features state (for toggle controls)
  */
  featureStateKey?: keyof DevFeaturesState;
};

export interface FeatureToggle extends Omit<Feature, "setEnabledAction"> {
  /** Whether the feature is currently enabled */
  isEnabled: boolean;

  /** Callback when the feature is toggled */
  onToggle: TogglesCallback;
}

export interface DevToolItem extends Omit<DevTool, "action"> {
  /** Callback function to execute when the tool is used */
  onClick?: ButtonsCallback;

  /** Callback function to execute when the tool is toggled (for toggle controls) */
  onToggle?: TogglesCallback;

  /** Whether the tool is currently enabled (for toggle controls) */
  isEnabled?: boolean;
}

/** Union type for items in the features table */
export type TableItem = FeatureToggle | DevToolItem;

/** Headers for the features table */
export const TABLE_HEADERS = ["Name", "Description", ""];

/**
 * Feature configuration object for development features
 */
export const FEATURES: Feature[] = [
  {
    label: "Dev Mode",
    description: "Enables features for developers and internal users that won't be released to customers",
    queryParam: QueryParams.enableDev,
    appStateKey: "isDevModeEnabled",
    setEnabledAction: setIsDevModeEnabled,
  },
  {
    label: "Alpha",
    description: "Enables features that are currently in development and may be incomplete, partially implemented, or not functioning as expected.",
    queryParam: QueryParams.enableAlpha,
    appStateKey: "isAlphaTestingEnabled",
    setEnabledAction: setIsAlphaTestingEnabled,
  },
  {
    label: "Beta",
    description: "Enables features that are mostly complete but not yet ready for release to customers.",
    queryParam: QueryParams.enableBeta,
    appStateKey: "isBetaTestingEnabled",
    setEnabledAction: setIsBetaTestingEnabled,
  },
];

export const DEV_TOOLS: DevTool[] = [
  {
    label: "Open Admin Panel",
    description: "Opens the admin panel in a new tab with the current workspace.",
    buttonText: "Open",
    action: "openAdminPanel",
    controlType: "button",
  },
  {
    label: "Clear Cookies",
    description: "Removes all stored cookies for the holobuilder domain.",
    buttonText: "Clear",
    action: "clearCookies",
    controlType: "button",
  },
  {
    label: "Reset FUE Flags",
    description: "Resets first user experience flags to show all splash-screens.",
    buttonText: "Reset",
    action: "resetFueFlags",
    controlType: "button",
  },
  {
    label: "Send Error to Sentry",
    description: "Sends a test error to Sentry with a unique identifier for verification.",
    buttonText: "Send",
    action: "sendTestSentryError",
    controlType: "button",
  },
  {
    label: "Allow localize translations",
    description: "Allows sending translation strings to Localize from this environment, regardless of whether it’s a numbered environment or localhost. This feature remains active only until the page is refreshed.",
    action: "allowLocalizeTranslations",
    featureStateKey: "isTranslationOverrideAllowed",
    controlType: "toggle",
  },
  {
    label: "Dark Mode",
    description: "Enables dark theme. All flat-ui components will use their dark theme variant.",
    action: "toggleDarkMode",
    featureStateKey: "isDarkModeEnabled",
    controlType: "toggle",
  },
];

/** Test error message constant that is sent to Sentry */
export const TEST_ERROR_MESSAGE = "DASHBOARD_TEST_SENTRY_ERROR";